import { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Lock } from "lucide-react";
import { useAuth } from "@/App";

const LOGO = "/assets/cravy-logo.png";

export default function AdminLogin() {
  const { user, checking, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  if (checking) return null;
  if (user) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email, password);
      toast.success("Welcome back.");
      navigate("/admin", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Login failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-cravy-ink cravy-grain flex items-center justify-center p-5 relative overflow-hidden font-body">
      <div className="absolute -top-32 -right-20 w-[480px] h-[480px] rounded-full bg-cravy-orange/15 blur-[120px]" />
      <div className="absolute -bottom-40 -left-32 w-[460px] h-[460px] rounded-full bg-cravy-orangeDeep/10 blur-[120px]" />

      <div className="relative w-full max-w-md">
        <Link to="/" className="inline-flex items-center mb-10" data-testid="login-logo-link">
          <img src={LOGO} alt="Cravy" className="h-14 w-14 object-contain" />
        </Link>

        <div className="rounded-3xl border border-white/8 bg-white/[0.03] p-8 backdrop-blur-sm">
          <div className="w-11 h-11 rounded-2xl bg-cravy-orange/15 text-cravy-orange flex items-center justify-center mb-6">
            <Lock className="w-5 h-5" />
          </div>
          <h1 className="font-display font-extrabold text-cravy-cream text-[28px] tracking-[-0.02em]">Sign in</h1>
          <p className="text-cravy-cream/60 text-[14px] mt-1.5">Manage your incoming smart-market leads.</p>

          <form onSubmit={submit} className="mt-7 space-y-4">
            <label className="block">
              <span className="block text-cravy-cream/70 text-[12px] uppercase tracking-[0.15em] mb-2 font-semibold">Email</span>
              <input
                type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="cravy-input" placeholder="you@cravyco.com" data-testid="login-email"
              />
            </label>
            <label className="block">
              <span className="block text-cravy-cream/70 text-[12px] uppercase tracking-[0.15em] mb-2 font-semibold">Password</span>
              <input
                type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                className="cravy-input" placeholder="••••••••" data-testid="login-password"
              />
            </label>
            <button type="submit" disabled={submitting} className="btn-orange w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full text-[15px] disabled:opacity-60" data-testid="login-submit">
              {submitting ? "Signing in…" : "Sign in"} <ArrowRight className="w-4 h-4" />
            </button>
          </form>
        </div>

        <div className="text-center mt-6">
          <Link to="/" className="text-cravy-cream/50 hover:text-cravy-cream text-[13px]" data-testid="login-back-home">← Back to site</Link>
        </div>
      </div>
    </div>
  );
}
